import { useEffect, useRef } from "react";

/**
 * Observes descendants with the `.reveal` class and adds `.in` when they enter the viewport.
 *
 * The effect dependency array is intentionally empty:
 *  - `IntersectionObserver` is a stable browser global (not a React-tracked value).
 *  - `ref.current`, `el`, and the IO callback's `entries` parameter are local to the effect/callback
 *    and never change identity in a way that should re-run this effect.
 * Re-running on every render would tear down and recreate the observer needlessly.
 */
export function useReveal() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const handleEntries = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add("in");
      });
    };

    const obs = new IntersectionObserver(handleEntries, { threshold: 0.12 });
    el.querySelectorAll(".reveal").forEach((node) => obs.observe(node));
    return () => obs.disconnect();
  }, []);

  return ref;
}
